import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { useLanguage } from '@/context/LanguageContext';
import ArticleImage from '@/components/ArticleImage';
import { Button } from '@/components/ui/button';
import { ArrowRight, Search, Filter, BookOpen } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await axios.get(`${API}/articles?published_only=true`);
        setArticles(response.data);
        setFilteredArticles(response.data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  useEffect(() => {
    let filtered = articles;

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(article => article.category === categoryFilter);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article => {
        const title = language === 'fr' ? article.title_fr : (article.title_de || article.title_fr);
        const excerpt = language === 'fr' ? article.excerpt_fr : (article.excerpt_de || article.excerpt_fr);
        return title.toLowerCase().includes(query) || excerpt.toLowerCase().includes(query);
      });
    }

    setFilteredArticles(filtered);
  }, [searchQuery, categoryFilter, articles, language]);

  const categories = [
    { value: 'all', label: t('Tous les articles', 'Alle Artikel') },
    { value: 'blog', label: 'Blog' },
    { value: 'experience', label: t('Expérience', 'Erfahrung') },
    { value: 'tips', label: t('Conseils', 'Tipps') }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-slate-100 to-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-bold tracking-widest uppercase text-blue-600 mb-4 block animate-fadeInUp">
            Blog
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 animate-fadeInUp delay-100 font-outfit">
            {t('Tous mes articles', 'Alle meine Artikel')}
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed animate-fadeInUp delay-200">
            {t(
              "Chaque article raconte une histoire et partage une réflexion ou un conseil.",
              "Jeder Artikel erzählt eine Geschichte und teilt eine Reflexion oder einen Rat."
            )}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-16 md:top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={t('Rechercher un article...', 'Artikel suchen...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full border-slate-200"
                data-testid="blog-search-input"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Filter className="w-5 h-5 text-slate-400" />
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full md:w-48 h-12 rounded-full" data-testid="blog-category-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredArticles.length > 0 ? (
            <>
              <p className="text-slate-600 mb-8">
                {filteredArticles.length} {t('article(s) trouvé(s)', 'Artikel gefunden')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredArticles.map((article, index) => (
                  <Link 
                    key={article.id} 
                    to={`/blog/${article.slug}`}
                    className="group"
                    data-testid={`blog-article-${index}`}
                  >
                    <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-hover h-full flex flex-col">
                      <div className="aspect-video overflow-hidden">
                        <ArticleImage
                          src={article.image_url}
                          alt={language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          containerClassName="aspect-video"
                        />
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                            article.category === 'experience' ? 'bg-blue-100 text-blue-600' :
                            article.category === 'tips' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {article.category === 'experience' ? t('Expérience', 'Erfahrung') :
                             article.category === 'tips' ? t('Conseils', 'Tipps') : 'Blog'}
                          </span>
                          <span className="text-xs text-slate-400">
                            {formatDate(article.created_at)}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 mb-3 font-outfit group-hover:text-blue-600 transition-colors">
                          {language === 'fr' ? article.title_fr : (article.title_de || article.title_fr)}
                        </h3>
                        <p className="text-slate-600 text-sm line-clamp-3 flex-1">
                          {language === 'fr' ? article.excerpt_fr : (article.excerpt_de || article.excerpt_fr)}
                        </p>
                        <div className="mt-4 flex items-center text-blue-600 font-medium text-sm">
                          {t('Lire la suite', 'Weiterlesen')}
                          <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform" />
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border border-slate-200">
              <BookOpen className="w-16 h-16 mx-auto mb-6 text-slate-300" />
              <h3 className="text-xl font-bold text-slate-900 mb-3 font-outfit">
                {searchQuery || categoryFilter !== 'all' 
                  ? t('Aucun article trouvé', 'Keine Artikel gefunden')
                  : t('Bientôt des articles !', 'Bald kommen Artikel!')}
              </h3>
              <p className="text-slate-600 mb-6">
                {searchQuery || categoryFilter !== 'all' 
                  ? t('Essaie avec d\'autres critères de recherche.', 'Versuche es mit anderen Suchkriterien.')
                  : t('Les premiers articles arrivent très bientôt.', 'Die ersten Artikel kommen sehr bald.')}
              </p>
              {(searchQuery || categoryFilter !== 'all') && (
                <Button 
                  variant="outline" 
                  onClick={() => { setSearchQuery(''); setCategoryFilter('all'); }}
                  data-testid="blog-reset-filters"
                >
                  {t('Réinitialiser les filtres', 'Filter zurücksetzen')}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
